import { motion } from "framer-motion";
import { X, Check, Frown, Smile } from "lucide-react";
import { PAINS, GAINS } from "../../lib/landing-data";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.05 } }),
};

export const ProblemStatement = () => {
  return (
    <section id="problem" className="relative py-20 sm:py-28 bg-gradient-to-b from-white via-purple-50/30 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider">
            The reality
          </span>
          <h2 className="font-heading mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
            Every seller knows this pain.
          </h2>
          <p className="mt-4 text-slate-600 text-base sm:text-lg">
            Listing on Flipkart & Meesho shouldn&rsquo;t feel like a full-time job. Here&rsquo;s what changes the moment you install Easy Lister.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-3xl border border-red-100 bg-white p-7 sm:p-9 shadow-sm"
            data-testid="problem-pains-card"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center">
                <Frown className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-red-500">Before Easy Lister</div>
                <div className="font-heading text-xl font-extrabold text-slate-900">The old grind</div>
              </div>
            </div>
            <ul className="space-y-3">
              {PAINS.map((p, i) => (
                <motion.li
                  key={p}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                  className="flex items-start gap-3 text-sm sm:text-base text-slate-600"
                >
                  <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-red-100 text-red-500 flex items-center justify-center">
                    <X className="w-3 h-3" strokeWidth={3} />
                  </span>
                  {p}
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-3xl border border-purple-200 bg-gradient-to-br from-purple-50 via-white to-fuchsia-50 p-7 sm:p-9 shadow-lg shadow-purple-600/5"
            data-testid="problem-gains-card"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-2xl bg-purple-600 text-white flex items-center justify-center shadow-lg shadow-purple-600/30">
                <Smile className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-purple-600">With Easy Lister</div>
                <div className="font-heading text-xl font-extrabold text-slate-900">The new normal</div>
              </div>
            </div>
            <ul className="space-y-3">
              {GAINS.map((g, i) => (
                <motion.li
                  key={g}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                  className="flex items-start gap-3 text-sm sm:text-base text-slate-700"
                >
                  <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-purple-600 text-white flex items-center justify-center shadow shadow-purple-600/30">
                    <Check className="w-3 h-3" strokeWidth={3} />
                  </span>
                  {g}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
